import { useEffect, useMemo, useState } from 'react';
import { fetchUnitDetails, fetchUnitMeters, fetchUnitFiles } from '../services/unit.api';
import { dash, fmtDateTimeGB, computeCounts, getOperatorDisplay } from '../utils/unitDetails.helpers';

export function useUnitDetails(unitName) {
  const [loading, setLoading] = useState(true);
  const [unitData, setUnitData] = useState(null);
  const [evnData, setEvnData] = useState([]);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    let alive = true;

    async function run() {
      setLoading(true);
      try {
        const [unit, meters, filesRes] = await Promise.all([
          fetchUnitDetails(unitName),
          fetchUnitMeters(unitName),
          fetchUnitFiles(unitName),
        ]);
        if (!alive) return;
        setUnitData(unit ?? null);
        setEvnData(Array.isArray(meters) ? meters : []);
        setFiles(Array.isArray(filesRes) ? filesRes : []);
      } catch (e) {
        if (!alive) return;
        console.error('Unit details load failed', e);
        setUnitData(null);
        setEvnData([]);
        setFiles([]);
      } finally {
        if (alive) setLoading(false);
      }
    }

    if (unitName) run();
    return () => { alive = false; };
  }, [unitName]);

  const { evnCount, meterCount: computedMeterCount } = useMemo(
    () => computeCounts(evnData),
    [evnData]
  );

  const operatorValue = useMemo(() => getOperatorDisplay(unitData), [unitData]);

  const summaryItems = useMemo(() => {
    const meterCount = unitData?.metersCount ?? computedMeterCount;
    return [
      ['Operator', operatorValue],
      ['Effective from', fmtDateTimeGB(unitData?.effectiveFrom)],
      ['Effective to', fmtDateTimeGB(unitData?.effectiveTo)],
      ['EVN Count', evnCount],
      ['Meter Count', meterCount],
    ];
  }, [unitData, operatorValue, evnCount, computedMeterCount]);

  return {
    loading,
    unitData,
    evnData,
    files,
    dash,
    operatorValue,
    summaryItems,
  };
}
